import config from "../config/config";

const {
    URL: { POTENCIAL_TALENTO_APP_URL_BACK, POTENCIAL_TALENTO_APP_URL_AUTH },
} = config;

const MESSAGE_ERROR = {
    estado: 0,
    message: "No se pudo realizar la petición",
};

const pendingRequests = {};
let activeRequests = 0;

const notifyGlobalLoading = (isLoading) => {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
        window.dispatchEvent(
            new CustomEvent("global-loading", {
                detail: { isLoading },
            }),
        );
    }
};

const startRequest = () => {
    activeRequests += 1;
    if (activeRequests === 1) {
        notifyGlobalLoading(true);
    }
};

const endRequest = () => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
        notifyGlobalLoading(false);
    }
};

/**
 * # Connection
 * Función que realiza la conexión con el servidor
 *
 * Parametros:
 *
 * - method: método de la petición (GET, POST, PUT, DELETE)
 * - url: url de la petición
 * - extraHeaders: headers adicionales
 * - body: cuerpo de la petición
 * - formDataMode: si es true, se enviará el cuerpo como un formulario
 * - signal: se usa para abortar la petición
 * - abortRepect: si es true, se cancela al repetir la misma petición
 * - responseType: tipo de respuesta esperada ("json", "blob", "text")
 * @param {*} param
 * @returns
 */
export default async function connection({
    method = "GET",
    url = "",
    extraHeaders = {},
    body = {},
    formDataMode = false,
    signal = null,
    abortRepect = false,
    responseType = "json",
    api = "back",
}) {
    const headers = new Headers({
        "Accept-Charset": "utf-8",
        "Access-Control-Allow-Origin": "*",
    });

    // No establecer Content-Type manualmente si es FormData
    if (!formDataMode) {
        headers.append("Content-Type", "application/json");
    }

    // Añadir token de auth desde localStorage si existe
    if (typeof window !== "undefined" && window.localStorage) {
        try {
            const rawUserData = window.localStorage.getItem("potencial-talento-user-data");
            if (rawUserData) {
                const parsedUserData = JSON.parse(rawUserData);
                if (parsedUserData?.token) {
                    headers.append("Authorization", `Bearer ${parsedUserData.token}`);
                }
            }
        } catch (e) {
            console.error("Error leyendo token desde localStorage", e);
        }
    }

    if (Object.keys(extraHeaders).length !== 0) {
        for (let headers_key in extraHeaders) {
            headers.append(headers_key, extraHeaders[headers_key]);
        }
    }

    if (abortRepect && pendingRequests[url]) {
        pendingRequests[url].abort();
    }

    const controller = new AbortController();
    signal = controller.signal;
    pendingRequests[url] = controller;

    const options = {
        method: method,
        headers: headers,
        redirect: "follow",
        signal: signal,
        credentials: 'include'
    };

    if (body && (method === "POST" || method === "PUT")) {
        options.body = formDataMode ? body : JSON.stringify(body);
    }
    let response;
    const isAuthRequest = api === "auth";
    const maskAuthBody = (value) => {
        if (value == null || typeof value !== "object") return value;
        if (Array.isArray(value)) return value.map(maskAuthBody);
        return Object.fromEntries(
            Object.entries(value).map(([key, val]) => [
                key,
                ["password", "repeatPassword", "token", "refresh_token"].includes(key)
                    ? "***"
                    : maskAuthBody(val),
            ]),
        );
    };
    try {
        startRequest();
        const baseUrl =
            api === "auth"
                ? POTENCIAL_TALENTO_APP_URL_AUTH
                : POTENCIAL_TALENTO_APP_URL_BACK;
        const requestUrl = `${baseUrl}${url}`;

        if (isAuthRequest) {
            console.group(`[Auth Debug] HTTP - request @ ${new Date().toISOString()}`);
            console.log("method:", method);
            console.log("url:", requestUrl);
            console.log("origin:", typeof window !== "undefined" ? window.location.origin : null);
            console.log("body:", maskAuthBody(body));
            console.groupEnd();
        }

        const fetchData = await fetch(
            requestUrl,
            options,
        );
        // Manejo del responseType
        if (responseType === "json") {
            response = await fetchData.json();
        } else if (responseType === "blob") {
            response = await fetchData.blob();
        } else if (responseType === "text") {
            response = await fetchData.text();
        } else {
            throw new Error(`responseType '${responseType}' no es soportado.`);
        }
        // if (fetchData?.status === 401) {
        //   localStorage.removeItem(USER_LOCALSTORAGE_KEY);
        // }
        if (!response?.status) {
            response.status = fetchData.status;
        }

        if (isAuthRequest) {
            console.group(`[Auth Debug] HTTP - response @ ${new Date().toISOString()}`);
            console.log("httpStatus:", fetchData.status);
            console.log("ok:", fetchData.ok);
            console.log("payload:", maskAuthBody(response));
            console.groupEnd();
        }
    } catch (error) {
        if (isAuthRequest) {
            console.group(`[Auth Debug] HTTP - error @ ${new Date().toISOString()}`);
            console.log("name:", error?.name);
            console.log("message:", error?.message);
            console.groupEnd();
        }
        if (error.name === "AbortError") {
            response = {
                ...MESSAGE_ERROR,
                message: "Petición cancelada",
                codeError: 103,
            };
        } else {
            response = { ...MESSAGE_ERROR, message: error.message };
        }
    } finally {
        endRequest();
        delete pendingRequests[url];
        return response;
    }
}
